// Every word on the accountability log (docs/mastermind-admin-plan.md 4.4), in 1 place, so the words
// test can check them all (tests/e2e/accountability-log.spec.js): British English, numerals, straight
// quotes, no exclamation marks and no em or en dashes. It's written to the member, in the second
// person. It never names a guarantee or evidence. Pure: no imports.
//
// Adam, 18 Oct, 15:30: the log is accountability, never the guarantee. It says plainly what's done and what isn't:
// an action not done on time is not done, and an action carried over is "Not done, carried over to month N", counted
// against them in the month it was set. Adam, 18 Oct, 15:45 (levels.js): On track only with every session attended
// live and every action due so far done; a session caught up on the recording still counts as missed; anything else
// is A little behind, Behind or Well behind, with the reasons. A measure, a month or a box in the strip that has
// slipped says Slipped, so the 4 levels are the 1 overall verdict (the Accelerator critique's member 10, 20 Oct).
// Every slip counts for the whole term (Adam's answer to question 2, 20 Oct), so no push promises a quick way back on
// track, and each push follows what slipped: actions, sessions or both (member 16). No percentage, limit, threshold
// or rule, on the member's page or on Adam's tab: the guarantee's rules stay in Masterminds, apart from the log.
// Adam can set a member's level by hand (his answer to question 2; migration 80). The member reads only the level's
// own words, never that it was set by hand, by whom or why; where the level sits outside its band of slips, the words
// that name a count give way to the count-free ones (LOG.pushSet, REFOCUS.headingSet: docs/accelerator-ux-words.md 8).

const s = (n, one, many) => (n === 1 ? one : many)
const times = n => `${n} ${s(n, 'time', 'times')}`
const sessions = n => `${n} ${s(n, 'session', 'sessions')}`
const itIsnt = n => s(n, "so it isn't counted", "so they aren't counted")

// The sessions missed, and whether the recordings were watched
const recordings = n => s(n, 'the recording', 'the recordings')
const missedLine = (missed, late, unit = true) => {
  const notWatched = missed - late
  const n = unit ? sessions(missed) : String(missed)
  if (!late) return `${n} missed and ${recordings(missed)} not watched.`
  if (!notWatched) return `${n} missed and ${recordings(missed)} watched late.`
  return `${n} missed: ${notWatched} ${s(notWatched, 'recording', 'recordings')} not watched and ${late} watched late.`
}

// What slipped: 'actions', 'sessions', 'both', or 'never' (sessions held and none attended live), so the push and
// the questions follow it (member 16, 20 Oct)
export const causeOf = ({ never, sessionsMissed, notDone } = {}) => {
  if (never) return 'never'
  if (sessionsMissed && notDone) return 'both'
  // nothing slipped: only a level set by hand is behind with no slips, and it gets the general push and questions
  if (!sessionsMissed && !notDone) return 'both'
  return sessionsMissed ? 'sessions' : 'actions'
}

// Sessions not attended live, and how many were caught up on the recording ("3 sessions missed, 1 caught up on the
// recording.")
const missedPhrase = (n, caught) => {
  if (!caught) return `${sessions(n)} missed.`
  if (caught === n) return `${sessions(n)} missed, caught up on ${recordings(n)}.`
  return `${sessions(n)} missed, ${caught} caught up on the recording.`
}

// Lower case a reason's first letter when it follows a colon ("the purchase fell through"), unless the
// word is a name or an initial ("Leeds", "HMO")
export const asClause = text => {
  const t = String(text || '').trim().replace(/[.\s]+$/, '')
  if (!t) return t
  return /^[A-Z][a-z']/.test(t) && !/^(I|I'm|I've|Adam)\b/.test(t) ? t[0].toLowerCase() + t.slice(1) : t
}

export const LOG = {
  title: 'Accountability log',
  print: 'Print or save as PDF',
  upToDate: (time, day) => `Up to date at ${time} on ${day}.`,
  personLine: ({ name, group, joined, month, months }) => [
    name, group, joined ? `Joined ${joined}` : null,
    // the membership month, apart from the month of actions on this month's card (monthActions): the Accelerator
    // critique's admin 2, on the member side (Adam's yes of 22 Oct)
    month ? (month > months ? `Past your first ${months} months` : `Membership month ${month} of ${months}`) : null,
  ].filter(Boolean).join('. ') + '.',
  empty: "Your accountability log starts once your first month's actions are set at a session.",
  asItWas: (day, time) => `As it was on ${day} at ${time}.`,
  printFoot: (name, day) => `${name}, accountability log, ${day}`,
  docTitle: name => `${name}, accountability log`,

  // 2. Where you stand
  standH: 'Where you stand',
  // The 4 levels and Getting started (levels.js has how many slips each allows, Adam, 18 Oct, 15:45)
  state: { on_track: 'On track', a_little_behind: 'A little behind', behind: 'Behind', well_behind: 'Well behind', getting_started: 'Getting started' },
  gettingStarted: 'Your log fills in from your first session: what you commit to, what you get done and the sessions you come to.',
  onTrack: ({ due, held }) => [
    held ? `every session attended live (${held} of ${held})` : null,
    due ? `every action done (${due} of ${due})` : null,
  ].filter(Boolean).join(' and ').replace(/^./, c => c.toUpperCase()) + '.',
  // what's slipped, plainly ("2 sessions missed. 4 actions not done in earlier months."): the actions are the months
  // Adam has reviewed, never this month's, which aren't due yet (member 6, 20 Oct)
  slipped: ({ sessionsMissed, caught, notDone, held, never }) => [
    never ? `${sessions(held)} held and none attended live.` : sessionsMissed ? missedPhrase(sessionsMissed, caught) : null,
    notDone ? `${notDone} ${s(notDone, 'action', 'actions')} not done in earlier months.` : null,
  ].filter(Boolean).join(' '),
  // a short push in Adam's voice, for each level, picked by what slipped (causeOf: member 16). Every slip counts for
  // the whole term (Adam's answer to question 2, 20 Oct), so none promises a way back on track: each says what to do
  // next. level: levels.js levelOf's answer (slips, sessionsMissed, notDone, never)
  push: {
    on_track: () => "You're doing what you committed to. Keep it up.",
    a_little_behind: (level = {}) => ({
      actions: level.slips === 1
        ? "You've let 1 action slip. Don't let it become a habit: get this month's done before your next session."
        : "You've let a couple of actions slip. Don't let it become a habit: get this month's done before your next session.",
      sessions: level.slips === 1
        ? "You've missed a session. Be there live at the next one."
        : "You've missed a couple of sessions. Be there live at every one from here.",
    })[causeOf(level)] || "You've let a couple of things slip. Be at your next session live, with this month's actions done.",
    behind: (level = {}) => ({
      actions: "You're falling behind on the actions you committed to. Do the work.",
      sessions: "You're falling behind on turning up. Be there live at every session.",
    })[causeOf(level)] || "You're falling behind on what you committed to. Turn up and do the work.",
    well_behind: (level = {}) => ({
      never: "You're well behind. You haven't been at a session live, and that's where the work gets set. Message Adam if something's in the way.",
      actions: "You're well behind. You're not doing the actions you need to get the results you joined for. Message Adam if something's in the way.",
      sessions: "You're well behind. You're not at enough sessions to get the results you joined for. Message Adam if something's in the way.",
    })[causeOf(level)] || "You're well behind. You're not doing enough to get the results you joined for. Message Adam if something's in the way.",
  },
  // a level set by hand outside its own band of slips (levels.js withSet), so the words that name a count would be
  // wrong: the push that names none (docs/accelerator-ux-words.md 8, for Adam's yes before release)
  pushSet: {
    a_little_behind: "You've let some things slip. Be at your next session live, with this month's actions done.",
  },
  carriedMeans: 'Carried over means it wasn\'t done on time. It counts as not done until you do it, then as done late.',
  caughtMeans: 'Catching up on the recording still counts as a missed session.',
  goalToDo: 'Agree your goal with Adam at your next session.',

  // 3. The 4 measures. A measure that has slipped says Slipped, never Behind, which is 1 of the 4 levels (member 10);
  // a payment overdue says Overdue
  measuresH: 'Your measures',
  mark: { on: 'On track', off: 'Slipped', todo: 'To do', overdue: 'Overdue' },
  sessions: {
    h: 'Sessions',
    label: 'attended live',
    excused: n => `${n} agreed with Adam in advance, ${itIsnt(n)}.`,
    paused: n => `${n} while your membership was paused, ${itIsnt(n)}.`,
    unrecorded: n => `${n} with no register taken, ${itIsnt(n)}.`,
    none: 'None yet',
    noneLine: 'Your sessions count from your first one.',
  },
  actions: {
    h: 'Actions',
    label: 'actions done',
    // "4 of 10 actions done. 6 not done (carried over)."
    detail: ({ done, due, notDone, carried, part }) => {
      const head = `${done} of ${due} ${s(due, 'action', 'actions')} done.`
      if (!notDone) return head
      const why = [carried ? (carried === notDone ? 'carried over' : `${carried} carried over`) : null,
        part ? (part === notDone ? 'only part done' : `${part} only part done`) : null].filter(Boolean).join(', ')
      return `${head} ${notDone} not done${why ? ` (${why})` : ''}.`
    },
    // carried into this month and not ticked yet: not done on time, and still not done
    carried: n => `${n} carried over into this month and still not done.`,
    due: n => `${n} due at your next session.`,
    // the session that reviews them has been held, and Adam hasn't recorded the review yet
    dueReview: n => `${n} waiting for Adam's review.`,
    setAside: n => `${n} set aside by agreement, ${itIsnt(n)}.`,
    none: 'None reviewed yet',
    noneLine: 'Your first actions are reviewed at your next session.',
    barLabel: 'Done and not done',
  },
  goal: {
    h: 'Goal',
    agreed: 'Agreed',
    reached: 'Reached',
    notYet: 'Not agreed yet',
    agreedLine: day => `Agreed with Adam on ${day}.`,
    reachedLine: day => `Reached on ${day}.`,
    toDoLine: 'Agree it with Adam at your next session.',
  },
  membership: {
    h: 'Membership',
    upToDate: 'Up to date',
    upToDateLine: "Your membership hasn't been suspended for a missed payment.",
    suspended: 'Suspended',
    suspendedLine: spans => `Suspended ${spans} for a missed payment.`,
    paused: spans => `Paused ${spans}.`,
    // overdue (member 13, 20 Oct): with the day it's open until on the member's own page, from their access; Adam's
    // tab and a kept copy don't have it, so they say it without a day. On a place someone else pays for, no card
    overdueH: 'Payment overdue',
    overdueLine: day => (day ? `Update your card by ${day} to keep everything open.` : 'Update your card to keep everything open.'),
    overdueOther: day => (day ? `Everything stays open until ${day} while it's sorted.` : "Everything stays open while it's sorted."),
  },
  // "from 3 to 12 Nov 2026", "since 3 Nov 2026"
  span: (from, to) => (to ? `from ${from} to ${to}` : `since ${from}`),

  // 4. The 12 months
  stripH: months => `Your ${months} months`,
  stripMark: {
    on_track: 'On track', off_track: 'Slipped', paused: 'Paused', in_progress: 'In progress', no_actions: 'No actions set', to_come: 'To come',
  },
  stripAria: (n, word) => `Month ${n}, ${word.toLowerCase()}`,
  legend: ['on_track', 'off_track', 'paused', 'in_progress', 'no_actions', 'to_come'],

  // 5. Month by month
  monthsH: 'Month by month',
  month: n => `Month ${n}`,
  // the open month's card: its month of actions, as admin's This month card has it, apart from the membership month in
  // the head (personLine)
  monthActions: n => `Month ${n}'s actions`,
  monthMark: { on_track: 'On track', off_track: 'Slipped', paused: 'Paused', in_progress: 'In progress', no_actions: 'No actions set' },
  offActionsLine: (done, due) => `Slipped: ${done} of ${due} ${s(due, 'action', 'actions')} done, ${due - done} not done.`,
  offSessionLine: (n, caught = 0) => `Slipped: ${caught ? missedPhrase(n, caught) : missedLine(n, 0)}`,
  setAtLine: (day, how) => `Set at the session on ${day}: ${how}.`,
  sessionLine: (day, how) => `Session on ${day}: ${how}.`,
  setOn: day => `Set on ${day}.`,
  how: {
    attended: 'you were there',
    caught_up: day => `you caught up on ${day}`,
    missed: 'missed',
    missedLate: day => `missed, caught up on ${day}`,
    excused: 'agreed with Adam in advance',
    paused: 'your membership was paused',
    unrecorded: "no register taken, so it isn't counted",
  },
  // "Reviewed at the session on Sun 25 Oct: 3 of 4 done. 1 not done, carried to month 3."
  reviewed: ({ atSession, day, counts, carried, next }) => {
    const head = atSession ? `Reviewed at the session on ${day}` : `Reviewed on ${day}`
    const done = counts.done || 0
    const part = counts.part || 0
    const not = counts.not || 0
    const finished = done + part + not
    if (!finished) return `${head}.`
    const rest = [part ? `${part} part done` : null, not ? `${not} not done` : null].filter(Boolean).join(' and ')
    const moved = carried && next ? (carried >= part + not ? `, carried to month ${next}` : `, ${carried} of them carried to month ${next}`) : ''
    return `${head}: ${done} of ${finished} done.${rest ? ` ${rest}${moved}.` : ''}`
  },
  openLine: (ticked, total, nextDay) => `${ticked} of ${total} ticked so far. Reviewed at your next session${nextDay ? ` on ${nextDay}` : ''}.`,
  // the session that reviews the month is under way, before its register: never "next", as the home's "Due at your
  // session on Sun 25 Oct." (the review of 22 Oct's small fixes)
  openNowLine: (ticked, total, day) => `${ticked} of ${total} ticked so far. Reviewed at your session on ${day}.`,
  // the session that reviews the month has been held, and Adam hasn't recorded the review yet
  openReviewLine: (ticked, total, day) => `${ticked} of ${total} ticked. Adam's review of the ${day} session is still to come.`,
  beforeH: first => (first ? `Before month ${first}` : 'Sessions so far'),
  noActions: 'No actions set this month.',
  afterTermH: months => `After your first ${months} months`,
  afterTermLine: 'Every action and session here counts too.',
  copied: (from, to, day) => `${from === to ? `Month ${from} was` : `Months ${from} to ${to} were`} recorded on the old members site and copied across on ${day}, so ${from === to ? 'it shows' : 'they show'} how each action went but not the day it was ticked.`,

  // An action's mark and its detail line
  actionMark: {
    done: 'Done', done_late: 'Done late', part_done: 'Not done', not_done: 'Not done', carried: 'Still not done',
    set_aside: 'Set aside', due: 'Due', ticked: 'Ticked',
  },
  ticked: day => `You ticked it on ${day}.`,
  doneAtReview: day => `Done at the review on ${day}.`,
  doneAtReviewNoDay: 'Done at the review.',
  // a copied month keeps no tick day, so a done action there never says when
  doneCopied: 'Done.',
  doneLate: (first, month, hops) => `Set in month ${first}, done in month ${month}, carried over ${times(hops)}.`,
  partDone: day => `Only part done at the review${day ? ` on ${day}` : ''}, so it counts as not done.`,
  notDone: day => (day ? `Not done at the review on ${day}.` : 'Not done at the review.'),
  notDoneAfter: (hops, day) => `Carried over ${times(hops)}, then not done at the review${day ? ` on ${day}` : ''}.`,
  // a carried-over action, in the month it was set: not done (Adam, 18 Oct)
  carriedTo: (to, part) => (part ? `Only part done, so not done. Carried over to month ${to}.` : `Not done, carried over to month ${to}.`),
  carriedOpen: (first, hops) => `Set in month ${first} and not done on time, so carried over ${times(hops)}.`,
  stillNotDone: 'Still not done.',
  setAside: (day, reason) => `Set aside by agreement on ${day}${reason ? `: ${asClause(reason)}` : ''}.`,
  due: 'Due at your next session.',
  dueTicked: day => `You ticked it on ${day}. It's reviewed at your next session.`,
  // the open month's ticks: the day, or none when the old site's copy didn't keep it; the review to come
  youTicked: day => (day ? `You ticked it on ${day}.` : 'You ticked it.'),
  atNextSession: "It's reviewed at your next session.",
  reviewToCome: "Adam's review is still to come.",
  notTickedReview: "Not ticked. Adam's review is still to come.",
  note: note => `Your note: ${note}`,
  unticked: (a, b) => `You ticked it on ${a}, then unticked it on ${b}.`,
  updated: day => `Updated on ${day}.`,

  // 6. Wins
  winsH: 'Your wins',
  winsCount: n => `${n} ${s(n, 'win', 'wins')} logged.`,
  winLine: (category, day) => `${category}, ${day}.`,
  winMore: 'More about it',
  winsNone: 'No wins logged yet. Add them on your home page as they happen.',
  category: { deal: 'Deal', finance: 'Finance', income: 'Income', marketing: 'Marketing', milestone: 'Milestone' },

  keptSince: day => `The hub has kept this log since ${day}.`,
}

// The member's page around the measures (Adam, 18 Oct): attendance as plain facts
export const LOG_MEMBER = {
  // the attendance flag: sessions missed, and whether the recording was watched ("2 sessions missed and the
  // recordings not watched.")
  missed: missedLine,
  sessionsDetail: ({ live, caught, missed, late }) => {
    const head = `${live} attended live${caught ? `, ${caught} missed and caught up on the recording` : ''}.`
    const notWatched = missed - late
    const tail = missed
      ? [notWatched ? `${notWatched} missed and ${recordings(notWatched)} not watched` : null, late ? `${late} missed and ${recordings(late)} watched late` : null].filter(Boolean).join(', ') + '.'
      : 'None missed.'
    return `${head} ${tail}`
  },
  // how a session went, in a month's line
  how: {
    caught_up: day => `missed, caught up on the recording on ${day}`,
    missed: 'missed, recording not watched',
    missedLate: day => `missed, recording watched late on ${day}`,
  },
  // the words in a missed session's line that link to its recording, on screen (member 9, 20 Oct)
  notWatched: 'recording not watched',
  // the actions carried into this month and not ticked yet, named, without their own full stops (member 6, 20 Oct)
  carriedNamed: bodies => {
    const q = bodies.map(b => `"${String(b).trim().replace(/[.\s]+$/, '')}"`)
    const list = q.length > 1 ? `${q.slice(0, -1).join(', ')} and ${q[q.length - 1]}` : q[0]
    return `Carried into this month and still not done: ${list}.`
  },
  // the link from the facts to the first month with an action not done (member 6)
  seeWhich: 'See which in your log',
  aboutH: 'About this log',
  foot: email => `This log is kept from what's recorded at each session. If anything looks wrong, email ${email}.`,
}

// The member's page around the log (the product's look)
export const LOG_PAGE = {
  viewLog: 'View accountability log',
  loadFailed: "Your accountability log didn't load",
  printHint: 'In the print box, choose Save as PDF. Turn off Headers and footers if your browser shows them.',
  // a month that went cleanly, folded to its heading on a phone (member 8, 20 Oct)
  show: 'Show',
  hide: 'Hide',
  showMonth: n => `Show month ${n}`,
  hideMonth: n => `Hide month ${n}`,
}

// The refocus card (Adam, 18 Oct, 16:12): on the member's home under their goal and at the top of their log, only when
// they're A little behind, Behind or Well behind. Their goal back to them, what's slipped, and 3 questions to ask
// themselves. Never a rule, a limit or a percentage. The heading starts with the level's own word, so the card and
// the pill never say it 2 ways (member 3, 20 Oct), and the questions follow what slipped (causeOf: member 16).
export const REFOCUS = {
  heading: {
    a_little_behind: slips => (slips === 1 ? 'A little behind. 1 thing has slipped' : 'A little behind. A couple of things have slipped'),
    behind: 'Behind. Time to refocus',
    well_behind: "Well behind. You're not doing enough",
  },
  // a level set by hand outside its own band of slips: the heading that names no count (docs/accelerator-ux-words.md 8)
  headingSet: {
    a_little_behind: 'A little behind. Keep on top of it',
  },
  goal: text => `Your goal: "${text}"`,
  noGoal: 'Agree your goal with Adam at your next session. Everything you do here builds towards it.',
  only: "You'll only get there by doing every action you commit to and turning up to every session.",
  askH: 'Take 2 minutes and ask yourself:',
  asks: {
    actions: ["What's stopped me doing the work?", 'What will I do differently before the next session?', "What's 1 action I can finish this week?"],
    sessions: ["What's kept me from the sessions?", "What do I need to move so I'm there live next time?", 'Have I watched the recording of what I missed?'],
    both: ["What's stopped me turning up and doing the work?", 'What will I do differently before the next session?', "What's 1 action I can finish this week?"],
    never: ["What's kept me from the sessions?", "What do I need to move so I'm there live next time?", "What's 1 action I can finish this week?"],
  },
  help: "If something's in the way, message Adam.",
}
